/**
 * The campaign builder and board (admin doc 26 §3.4, CSR-033).
 *
 * **Two figures, always.** The audience reads "matched N · will receive M": criteria first, then
 * the consent ledger. A campaign that showed one number would let somebody believe they were
 * messaging people who never agreed to hear from us.
 *
 * **No broadcast without proof.** Send stays disabled until a test-send has rendered every
 * channel and locale through the real render path. The server enforces the same rule (409
 * `test_send_required`), and also voids the proof when a template moves.
 *
 * **The operator types the reach.** The confirm phrase is the live will-receive count, and the
 * server refuses the send if the audience moved since it was read.
 *
 * The criteria are the closed set and nothing else. There is no free-text audience field and no
 * upload, so neither can be offered here by mistake.
 */
import { useState } from 'react';
import { Button, StatusChip } from '../components/primitives';
import { DataTable, Pager } from '../components/data';
import { Input, Select, Toggle } from '../components/forms';
import { EmptyState, ErrorState, LoadingState } from '../components/feedback';
import { ConfirmAction, ScopeAction, Section } from '../components/kit';
import { errorMessage } from '../lib/api/client';
import type { Scope } from '../lib/rbac';
import {
  useCampaignAudience,
  useCampaignProgress,
  useCampaigns,
  useControlCampaign,
  useCreateCampaign,
  usePatchCampaign,
  useSendCampaign,
  useTestSendCampaign,
  type CampaignControl,
  type CampaignRow,
} from '../lib/api/hooks/campaigns';
import { useOfferScopeOptions, type OfferScopeOption } from '../lib/api/hooks/offers';
import { ScopePicker, scopeSummary } from './OfferScope';

const CHANNELS = ['push', 'whatsapp'] as const;
/** The one marketing-class event a campaign sends today; the server checks the class. */
const EVENT_TYPE = 'campaign.offer_announcement';
const ROLES = ['anyone', 'devotee', 'pandit'] as const;
/**
 * The locales a message renders in (`render.SUPPORTED_LOCALES`). Toggles rather than free text:
 * the server takes any 2–8 letter code, so a typed `xx` saved happily and narrowed the audience
 * to nobody who could ever match it.
 */
const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'hi', label: 'Hindi' },
] as const;

/** `name=value` per line → template tokens. Blank lines and lines without `=` are ignored. */
export function parseTokens(text: string): Record<string, string> {
  const tokens: Record<string, string> = {};
  for (const line of text.split('\n')) {
    const at = line.indexOf('=');
    if (at <= 0) continue;
    tokens[line.slice(0, at).trim()] = line.slice(at + 1).trim();
  }
  return tokens;
}

/** The form's view of a campaign: every field as the operator edits it. */
export interface CampaignDraft {
  name: string;
  channels: string[];
  languages: string[];
  role: string;
  days: string;
  tokens: string;
  cityIds: number[];
}

const EMPTY_DRAFT: CampaignDraft = {
  name: '',
  channels: ['push'],
  languages: [],
  role: 'anyone',
  days: '',
  tokens: '',
  cityIds: [],
};

/** A saved campaign back into the form, so a draft is edited from what the server holds. */
export function draftOf(c: Pick<CampaignRow, 'name' | 'channels' | 'criteria' | 'context'>): CampaignDraft {
  return {
    name: c.name,
    channels: [...c.channels],
    languages: [...(c.criteria.languages ?? [])],
    role: c.criteria.role ?? 'anyone',
    days: c.criteria.active_within_days == null ? '' : String(c.criteria.active_within_days),
    tokens: Object.entries(c.context)
      .map(([k, v]) => `${k}=${v}`)
      .join('\n'),
    cityIds: [...(c.criteria.city_ids ?? [])],
  };
}

/** Only the criteria that narrow the audience — an empty one is "everyone", so it is left out. */
export function criteriaOf(d: CampaignDraft): Record<string, unknown> {
  const criteria: Record<string, unknown> = {};
  if (d.languages.length > 0) criteria.languages = d.languages;
  if (d.role !== 'anyone') criteria.role = d.role;
  if (d.days.trim() !== '') criteria.active_within_days = Number(d.days);
  if (d.cityIds.length > 0) criteria.city_ids = d.cityIds;
  return criteria;
}

/** A blank day count is "any time"; anything else must be a whole number of days. */
export function daysError(days: string): string | undefined {
  return days.trim() === '' || /^[1-9]\d*$/.test(days.trim()) ? undefined : 'Whole days, 1 or more';
}

export function draftReady(d: CampaignDraft): boolean {
  return d.name.trim() !== '' && d.channels.length > 0 && daysError(d.days) === undefined;
}

/** What a saved draft narrows its audience to, in a sentence. */
export function describeCriteria(
  criteria: CampaignRow['criteria'],
  cities: readonly OfferScopeOption[] | undefined,
): string {
  const parts: string[] = [];
  if (criteria.languages?.length) {
    parts.push(
      criteria.languages
        .map((code) => LANGUAGES.find((l) => l.code === code)?.label ?? code)
        .join(' and '),
    );
  }
  if (criteria.role) parts.push(`${criteria.role}s only`);
  if (criteria.city_ids?.length) parts.push(`in ${scopeSummary(criteria.city_ids, cities, '')}`);
  if (criteria.active_within_days != null) {
    parts.push(`active in the last ${criteria.active_within_days} days`);
  }
  return parts.length === 0 ? 'Everyone who consented' : parts.join(' · ');
}

/** The controls a campaign in this state may use — the server's own transition table. */
export function controlsFor(status: string): CampaignControl[] {
  if (status === 'scheduled' || status === 'sending') return ['pause'];
  if (status === 'paused') return ['resume', 'cancel'];
  if (status === 'draft') return ['cancel'];
  return [];
}

export function statusToneOf(status: string): 'neutral' | 'warn' | 'success' | 'error' {
  if (status === 'done') return 'success';
  if (status === 'sending' || status === 'scheduled') return 'warn';
  if (status === 'cancelled') return 'error';
  return 'neutral';
}

const toggled = (list: readonly string[], item: string) =>
  list.includes(item) ? list.filter((i) => i !== item) : [...list, item];

/** One set of fields for a new draft and an edited one, so the two can never drift apart. */
function CampaignFields({
  draft,
  onChange,
  cities,
}: {
  draft: CampaignDraft;
  onChange: (d: CampaignDraft) => void;
  cities: readonly OfferScopeOption[];
}) {
  const set = (patch: Partial<CampaignDraft>) => onChange({ ...draft, ...patch });
  return (
    <>
      <Input density="admin" label="Name" value={draft.name} onChange={(name) => set({ name })} />
      <div style={{ display: 'flex', gap: 'var(--sp-md)' }}>
        {CHANNELS.map((channel) => (
          <Toggle
            key={channel}
            label={channel}
            on={draft.channels.includes(channel)}
            onChange={() => set({ channels: toggled(draft.channels, channel) })}
          />
        ))}
      </div>
      <div role="group" aria-label="Languages" style={{ display: 'flex', gap: 'var(--sp-md)' }}>
        {LANGUAGES.map((l) => (
          <Toggle
            key={l.code}
            label={l.label}
            on={draft.languages.includes(l.code)}
            onChange={() => set({ languages: toggled(draft.languages, l.code) })}
          />
        ))}
        <span>{draft.languages.length === 0 ? 'Every language' : ''}</span>
      </div>
      <Select label="Role" value={draft.role} onChange={(role) => set({ role })} options={ROLES} />
      <ScopePicker
        label="Cities"
        everywhere="Every city"
        options={cities}
        value={draft.cityIds}
        onChange={(cityIds) => set({ cityIds })}
      />
      <Input
        density="admin"
        label="Active within (days)"
        inputMode="numeric"
        value={draft.days}
        onChange={(days) => set({ days })}
        error={daysError(draft.days)}
      />
      <Input
        density="admin"
        label="Template tokens"
        helper="One per line, name=value. Never personal data."
        multiline
        value={draft.tokens}
        onChange={(tokens) => set({ tokens })}
      />
    </>
  );
}

function NewCampaign({ scope, cities }: { scope: Scope; cities: readonly OfferScopeOption[] }) {
  const create = useCreateCampaign();
  const [draft, setDraft] = useState<CampaignDraft>(EMPTY_DRAFT);

  const submit = () =>
    create.mutate(
      {
        name: draft.name,
        event_type: EVENT_TYPE,
        channels: draft.channels,
        criteria: criteriaOf(draft),
        context: parseTokens(draft.tokens),
      },
      /* The whole form clears: a kept audience under a blank name is the next draft's by accident. */
      { onSuccess: () => setDraft(EMPTY_DRAFT) },
    );

  return (
    <Section title="New campaign" note="Marketing class: consent, caps and quiet hours always apply">
      <div style={{ display: 'grid', gap: 'var(--sp-sm)' }}>
        <CampaignFields draft={draft} onChange={setDraft} cities={cities} />
        <ScopeAction
          scope={scope}
          need="growth"
          size="sm"
          disabled={!draftReady(draft) || create.isPending}
          onClick={submit}
        >
          Save draft
        </ScopeAction>
        {create.isError && (
          <ErrorState title="Draft not saved" cause={errorMessage(create.error)} inline />
        )}
      </div>
    </Section>
  );
}

/** Only what changed is sent: the server replaces any field it is given and voids the proof. */
export function draftPatch(before: CampaignDraft, after: CampaignDraft): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (after.name !== before.name) out.name = after.name;
  if (JSON.stringify(after.channels) !== JSON.stringify(before.channels)) out.channels = after.channels;
  const criteria = criteriaOf(after);
  if (JSON.stringify(criteria) !== JSON.stringify(criteriaOf(before))) out.criteria = criteria;
  const context = parseTokens(after.tokens);
  if (JSON.stringify(context) !== JSON.stringify(parseTokens(before.tokens))) out.context = context;
  return out;
}

function DraftEditor({
  campaign,
  cities,
  onDone,
}: {
  campaign: CampaignRow;
  cities: readonly OfferScopeOption[];
  onDone: () => void;
}) {
  const patch = usePatchCampaign();
  const before = draftOf(campaign);
  const [draft, setDraft] = useState<CampaignDraft>(before);
  const body = draftPatch(before, draft);
  const changed = Object.keys(body).length > 0;
  return (
    <div style={{ display: 'grid', gap: 'var(--sp-sm)' }}>
      <CampaignFields draft={draft} onChange={setDraft} cities={cities} />
      {campaign.test_sent_at && changed && (
        <span>Saving voids the test-send — the changed content needs a fresh one before Send.</span>
      )}
      <div style={{ display: 'flex', gap: 'var(--sp-sm)' }}>
        <Button
          size="sm"
          disabled={!changed || !draftReady(draft) || patch.isPending}
          onClick={() => patch.mutate({ id: campaign.id, body }, { onSuccess: onDone })}
        >
          Save changes
        </Button>
        <Button size="sm" variant="secondary" onClick={onDone}>
          Close editor
        </Button>
      </div>
      {patch.isError && (
        <ErrorState title="Draft not changed" cause={errorMessage(patch.error)} inline />
      )}
    </div>
  );
}

const CONTROL_LABEL: Record<CampaignControl, string> = {
  pause: 'Pause',
  resume: 'Resume',
  cancel: 'Cancel campaign',
};

function CampaignDetail({
  campaign,
  scope,
  cities,
}: {
  campaign: CampaignRow;
  scope: Scope;
  cities: readonly OfferScopeOption[];
}) {
  const live = campaign.status === 'sending' || campaign.status === 'scheduled';
  const audience = useCampaignAudience(campaign.id);
  const progress = useCampaignProgress(campaign.id, live);
  const testSend = useTestSendCampaign();
  const send = useSendCampaign();
  const control = useControlCampaign();
  const [confirming, setConfirming] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [editing, setEditing] = useState(false);

  const willReceive = audience.data?.will_receive;
  const reach = willReceive ?? 0;
  const isDraft = campaign.status === 'draft';

  return (
    <Section title={campaign.name} note={`${campaign.event_type} · ${campaign.channels.join(', ')}`}>
      <div style={{ display: 'grid', gap: 'var(--sp-sm)' }}>
        <p>Audience: {describeCriteria(campaign.criteria, cities)}</p>
        {audience.isSuccess && (
          <p>
            Matched {audience.data.matched} · will receive {audience.data.will_receive} (consented
            on a campaign channel; caps, quiet hours and missing addresses apply per message)
          </p>
        )}
        {audience.isError && (
          <ErrorState title="Audience not counted" cause={errorMessage(audience.error)} inline />
        )}
        {isDraft && editing && (
          <DraftEditor campaign={campaign} cities={cities} onDone={() => setEditing(false)} />
        )}
        {isDraft && !editing && (
          <div style={{ display: 'flex', gap: 'var(--sp-sm)' }}>
            <ScopeAction
              scope={scope}
              need="growth"
              size="sm"
              variant="secondary"
              onClick={() => setEditing(true)}
            >
              Edit draft
            </ScopeAction>
            <ScopeAction
              scope={scope}
              need="growth"
              size="sm"
              variant="secondary"
              onClick={() => testSend.mutate(campaign.id)}
            >
              Test-send
            </ScopeAction>
            <ScopeAction
              scope={scope}
              need="growth"
              size="sm"
              disabled={!campaign.test_sent_at || willReceive === undefined}
              onClick={() => setConfirming(true)}
            >
              Send
            </ScopeAction>
          </div>
        )}
        {isDraft && !campaign.test_sent_at && (
          <span>Send unlocks after a test-send of the content as it stands.</span>
        )}
        {testSend.isSuccess &&
          testSend.data.proofs.map((proof) => (
            <div key={`${proof.channel}-${proof.locale}`}>
              <strong>
                {proof.channel} · {proof.locale} · v{proof.template_version}
              </strong>
              : {proof.body}
            </div>
          ))}
        {testSend.isError && (
          <ErrorState title="Test-send refused" cause={errorMessage(testSend.error)} inline />
        )}
        {send.isError && (
          <ErrorState title="Send refused" cause={errorMessage(send.error)} inline />
        )}
        <div style={{ display: 'flex', gap: 'var(--sp-sm)' }}>
          {controlsFor(campaign.status).map((name) => (
            <ScopeAction
              key={name}
              scope={scope}
              need="growth"
              size="sm"
              variant={name === 'cancel' ? 'destructive' : 'secondary'}
              onClick={() =>
                name === 'cancel'
                  ? setCancelling(true)
                  : control.mutate({ id: campaign.id, control: name })
              }
            >
              {CONTROL_LABEL[name]}
            </ScopeAction>
          ))}
        </div>
        {control.isError && (
          <ErrorState title="Not changed" cause={errorMessage(control.error)} inline />
        )}
        {progress.isSuccess && progress.data.recipients > 0 && (
          <div>
            <p>{progress.data.recipients} recipients handed on</p>
            {Object.entries(progress.data.by_status).map(([status, count]) => (
              <StatusChip key={status} tone="neutral" label={`${status} ${count}`} />
            ))}
            {Object.entries(progress.data.suppressed_by_reason).map(([reason, count]) => (
              <StatusChip key={reason} tone="warn" label={`skipped: ${reason} ${count}`} />
            ))}
          </div>
        )}
      </div>
      <ConfirmAction
        open={confirming && willReceive !== undefined}
        title={`Send ${campaign.name}`}
        phrase={String(reach)}
        confirmLabel="Send"
        consequences={[
          `Messages ${reach} people on ${campaign.channels.join(' and ')}.`,
          'Quiet hours defer each message into its window; weekly caps still apply.',
          'Pausing takes effect at the next chunk; nothing already sent can be recalled.',
        ]}
        onCancel={() => setConfirming(false)}
        onConfirm={() => {
          setConfirming(false);
          send.mutate({ id: campaign.id, confirm_count: reach });
        }}
      />
      <ConfirmAction
        open={cancelling}
        title={`Cancel ${campaign.name}?`}
        phrase="CANCEL"
        tone="error"
        confirmLabel="Cancel campaign"
        consequences={[
          'Nobody else is messaged; anything already handed on stays sent.',
          'A cancelled campaign cannot be resumed or edited — the same message again is a new draft.',
        ]}
        onCancel={() => setCancelling(false)}
        onConfirm={() => {
          setCancelling(false);
          control.mutate({ id: campaign.id, control: 'cancel' });
        }}
      />
    </Section>
  );
}

export function CampaignsTab({ scope }: { scope: Scope }) {
  const [page, setPage] = useState(1);
  const campaigns = useCampaigns(page);
  const [selected, setSelected] = useState<number | null>(null);
  const chosen = campaigns.data?.items.find((c) => c.id === selected) ?? null;
  /* City names come from the offers' scope list: the growth role cannot read the geo console. */
  const cities = useOfferScopeOptions().data?.cities ?? [];

  return (
    <>
      <NewCampaign scope={scope} cities={cities} />
      {campaigns.isPending && <LoadingState message="Reading campaigns…" />}
      {campaigns.isError && (
        <ErrorState title="Could not read campaigns" cause={errorMessage(campaigns.error)} />
      )}
      {campaigns.isSuccess && campaigns.data.items.length === 0 && (
        <EmptyState
          icon="megaphone"
          title="No campaigns yet"
          body="Announcements are opt-in only — audiences count consent first."
          inline
        />
      )}
      {campaigns.isSuccess && campaigns.data.items.length > 0 && (
        <Section title="Campaigns" pad={false}>
          <DataTable
            density="compact"
            columns={[
              { key: 'name', label: 'Name' },
              { key: 'channels', label: 'Channels' },
              { key: 'state', label: 'State' },
              { key: 'proof', label: 'Proof' },
              { key: 'open', label: '' },
            ]}
            rows={campaigns.data.items.map((c) => ({
              id: c.id,
              name: c.name,
              channels: c.channels.join(', '),
              state: <StatusChip tone={statusToneOf(c.status)} label={c.status} />,
              proof: c.test_sent_at ? 'test-sent' : '—',
              open: (
                <Button size="sm" variant="secondary" onClick={() => setSelected(c.id)}>
                  Open
                </Button>
              ),
            }))}
          />
          <Pager
            page={page}
            pageSize={campaigns.data.page_size}
            total={campaigns.data.total}
            onPage={setPage}
          />
        </Section>
      )}
      {chosen && <CampaignDetail campaign={chosen} scope={scope} cities={cities} />}
    </>
  );
}
